/*************************************************************************
* ADOBE CONFIDENTIAL
* ___________________
*
* Copyright 2019 Adobe
* All Rights Reserved.
*
* NOTICE: All information contained herein is, and remains
* the property of Adobe and its suppliers, if any. The intellectual
* and technical concepts contained herein are proprietary to Adobe
* and its suppliers and are protected by all applicable intellectual
* property laws, including trade secret and copyright laws.
* Dissemination of this information or reproduction of this material
* is strictly forbidden unless prior written permission is obtained
* from Adobe.
**************************************************************************/
import logToConsole from "bundle/util/LoggingUtil";

export const LoggingLevel = {
    INFO: "info",
    ERROR: "error",
    WARN: "warn",
    DEBUG: "debug",
    PERF: "perf",
};

export const LoggingConstants = {
    APP_PERFORMANCE: "APP_PERF_DATA",
    EXTERNAL_EXPORT_ERROR: "externalExportError",
    EXTERNAL_IMPORT_ERROR: "externalImportError",
    CONVERSION_ERROR: "conversionError",
    CONVERSION_INPUT: "CONVERSION_INPUT",
    CONVERSION_START: "CONVERSION_START",
    CONVERSION_ASSET_IN_PROGRESS: "CONVERSION_IN_PROGRESS",
    CONVERSION_ASSET_IMPORT_SUCCESS: "CONVERSION_ASSET_IMPORT_SUCCESS",
    CONVERSION_ASSET_IMPORT_FAILED: "CONVERSION_ASSET_IMPORT_FAILED",
    CONVERSION_CANCEL: "CONVERSION_CANCEL",
    CONVERSION_UPSELL_SHOWN: "CONVERSION_UPSELL_SHOWN",
    CONVERSION_IN_PROGRESS: "CONVERSION_IN_PROGRESS",
    CONVERSION_SUCCESS: "CONVERSION_SUCCESS",
    CONVERSION_FAILED: "CONVERSION_FAILED",
    DROPIN_EVENT: "DROPIN_EVENT",
    ENABLE_SAVE_COMMENT: "ENABLE_SAVE_COMMENT",
    SAVE_COMMENT: "SAVE_COMMENT",
    SAVE_COMMENT_CONFLICT: "SAVE_COMMENT_CONFLICT",
    SAVE_COMMENT_ERROR: "SAVE_COMMENT_ERROR",
    SAVE_API: "SAVE_API",
};

const LOG_ENABLED_TYPE = "error,warn,perf,info";

export default class LoggingService {
    constructor() {
        this._config = {};
        this._loggers = {};
        this._logToConsole = window.location.search && window.location.search.indexOf("logToConsole") !== -1;
    }

    initialize(config) {
        this._config = config;
    }

    logExternal(...args) {
        if (this._logToConsole || this._config.logToConsole) {
            logToConsole(...args);
        }
    }

    initDCLogging(corePromise, loggerDefer) {
        if (!this._config.loggingUri) {
            return;
        }

        window.dcWebSessionId = this._config.sessionId;
        corePromise.then(core => {
            this._core = core;
            core.logging.configure(logConfig => {
                const origConsoleHandler = logConfig.streams.console.handler;
                const origServerHandler = logConfig.streams.server.handler;
                const origServerBatchHandler = logConfig.streams.server.batchHandler;
                const viewSDKAppVersion = this._config.viewSDKAppVersion;
                const sessionIdObj = { sessionId: this._config.sessionId, viewSDKAppVersion };
                if (this._config.analyticsInfo) {
                    if (this._config.analyticsInfo.callingApp) {
                        sessionIdObj.callingApp = this._config.analyticsInfo.callingApp;
                    }
                    if (this._config.analyticsInfo.tenantId) {
                        sessionIdObj.tenantId = this._config.analyticsInfo.tenantId;
                    }
                    if (this._config.analyticsInfo.externalUserId) {
                        sessionIdObj.externalUserId = this._config.analyticsInfo.externalUserId;
                    }
                }
                if (!sessionIdObj.tenantId) {
                    sessionIdObj.tenantId = this._config.eventOrigin || this._config.clientId;
                }
                if (!sessionIdObj.callingApp) {
                    sessionIdObj.callingApp = "dc-view-sdk";
                }
                if (!sessionIdObj.externalUserId) {
                    sessionIdObj.externalUserId = this._config.windowHref;
                }
                sessionIdObj.clientId = this._config.clientId;
                const newContext = context => ((context) ? { ...sessionIdObj, ...context } : { ...sessionIdObj });
                const changeLogLevelPerfToInfo = context => {
                    if (context && context.level === "perf") {
                        context.level = "info"; // eslint-disable-line
                    }
                };
                const logAllowed = level => LOG_ENABLED_TYPE.indexOf(level) > -1;
                const modifyHandler = (handler, ...args) => {
                    args[1] = newContext(args[1]); // eslint-disable-line
                    const isAllowed = logAllowed(args[2]);
                    if (isAllowed) {
                        changeLogLevelPerfToInfo(args[2]);
                    }
                    return isAllowed ? handler(...args) : () => {};
                };
                Object.assign(logConfig, {
                    serverOrigin: this._config.loggingUri,
                    serverAppInfo: "adobe-dc-view-sdk",
                    serverApiClient: "dc-view-sdk-api_browser",
                    streams: {
                        server: Object.assign(logConfig.streams.server, {
                            enable: () => this._config.internalLogToServerEnabled,
                            deferUntil: loggerDefer.promise,
                            handler: (...args) => modifyHandler(origServerHandler, ...args),
                            batchHandler: (...args) => {
                                const logs = args[0];
                                if (logs) {
                                    // eslint-disable-next-line no-param-reassign
                                    args[0] = logs.filter(log => {
                                        log[1] = newContext(log[1]); // eslint-disable-line
                                        const isAllowed = logAllowed(log[2]);
                                        if (isAllowed) {
                                            changeLogLevelPerfToInfo(log[1]);
                                        }
                                        return isAllowed;
                                    }); // eslint-disable-line
                                }
                                return origServerBatchHandler(...args);
                            },
                        }),
                        console: Object.assign(logConfig.streams.console, {
                            enable: () => this._config.internalLogToConsoleEnabled,
                            handler: (...args) => modifyHandler(origConsoleHandler, ...args),
                        }),
                    },
                });
            });
        });
    }

    getLogger(className) {
        if (!this._loggers[className] && this._core && this._core.logging) {
            this._loggers[className] = this._core.logging.getLogger(className);
        }
        return this._loggers[className];
    }

    getLogObject(level) { // eslint-disable-line
        return {
            level,
        };
    }

    emit(className, level, logMessage, logObject = {}) {
        const logger = this.getLogger(className);
        if (logger) {
            const logData = Object.assign(
                logObject,
                this.getLogObject(level),
            );
            logger.emit(logMessage, logData);
        } else {
            this.logExternal(logMessage, logObject);
        }
    }

    info(className, ...args) {
        const logger = this.getLogger(className);
        if (logger) {
            logger.info(...args);
        } else {
            this.logExternal("[INFO]", ...args);
        }
    }

    debug(className, ...args) {
        const logger = this.getLogger(className);
        if (logger) {
            logger.debug(...args);
        } else {
            this.logExternal("[DEBUG]", ...args);
        }
    }

    warn(className, ...args) {
        const logger = this.getLogger(className);
        if (logger) {
            logger.warn(...args);
        } else {
            this.logExternal("[WARNING]", ...args);
        }
    }

    error(className, ...args) {
        const logger = this.getLogger(className);
        if (logger) {
            logger.error(...args);
        } else {
            this.logExternal("[ERROR]", ...args);
        }
    }
}
